/**
 * Reusable file-upload validation helpers.
 *
 * Why this exists:
 * Checking only `file.type` (the browser-reported MIME type) is not enough.
 * Attackers can craft deceptive filenames such as:
 *   - image.png.svg   -> real extension is .svg (not allowed)
 *   - image.svg.png   -> looks like png but carries an svg segment
 *   - image.php.png    -> carries an executable .php segment
 * The browser may even report a "friendly" MIME type for some of these.
 *
 * These helpers validate BOTH:
 *   1. The real (final) file extension against an allow-list.
 *   2. The MIME type against an allow-list.
 *   3. That NO segment of the filename is a disallowed/executable extension
 *      (catches double-extension tricks like image.php.png).
 */

/** Map of allowed image extensions -> the MIME types we accept for each. */
const IMAGE_EXTENSION_MIME_MAP: Record<string, string[]> = {
  png: ["image/png"],
  jpg: ["image/jpeg", "image/jpg"],
  jpeg: ["image/jpeg", "image/jpg"],
};

/**
 * Extensions that must never appear ANYWHERE in a filename, even as an
 * intermediate segment (e.g. the ".php" in "image.php.png"). This blocks
 * common script/executable and vector/markup payloads disguised as images.
 */
const DISALLOWED_SEGMENT_EXTENSIONS = new Set<string>([
  "svg", "html", "htm", "xml", "php", "php3", "php4", "php5", "phtml",
  "js", "mjs", "jsx", "ts", "tsx", "exe", "sh", "bat", "cmd", "com",
  "jar", "py", "rb", "pl", "asp", "aspx", "jsp", "cgi", "dll", "msi",
  "bin", "scr", "vbs", "ps1",
]);

export interface FileValidationResult {
  valid: boolean;
  /** Human-readable message suitable for showing in a toast/alert. */
  message: string;
}

/** Split a filename into its dot-separated extension segments (lowercased). */
const getExtensionSegments = (fileName: string): string[] => {
  const parts = fileName.split(".");
  // First part is the base name, everything after a dot is an extension segment.
  return parts.slice(1).map((segment) => segment.trim().toLowerCase());
};

/** Returns the real (final) extension of a filename, lowercased. */
export const getFileExtension = (fileName: string): string => {
  const segments = getExtensionSegments(fileName);
  return segments.length ? segments[segments.length - 1] : "";
};

/**
 * Core validator. Given a file, the allowed extension->MIME map, and a
 * human-readable label of allowed types, returns whether the file is safe.
 */
const validateByExtensionMap = (
  file: File,
  extensionMimeMap: Record<string, string[]>,
  allowedLabel: string
): FileValidationResult => {
  if (!file) {
    return { valid: false, message: "No file selected." };
  }

  const fileName = file.name || "";
  const segments = getExtensionSegments(fileName);

  // Must have at least one extension.
  if (segments.length === 0) {
    return {
      valid: false,
      message: `Invalid file. Only ${allowedLabel} files are allowed.`,
    };
  }

  const allowedExtensions = Object.keys(extensionMimeMap);

  // Only the intermediate segments (everything before the final extension) are
  // relevant for double-extension detection. The final extension is validated
  // separately in step 3, so a single-extension file such as "image.svg" is
  // reported as an unsupported type rather than a "disguised" name.
  const intermediateSegments = segments.slice(0, -1);

  // 1. Reject if any INTERMEDIATE segment is a known-dangerous extension
  //    (double-extension tricks like image.php.png or image.svg.png).
  const hasDangerousIntermediate = intermediateSegments.some((segment) =>
    DISALLOWED_SEGMENT_EXTENSIONS.has(segment)
  );
  if (hasDangerousIntermediate) {
    return {
      valid: false,
      message: `Invalid file name. Files with multiple or disguised extensions are not allowed. Only ${allowedLabel} files are accepted.`,
    };
  }

  // 2. Reject if any INTERMEDIATE segment is not an allowed extension.
  //    A clean filename should look like "photo.png" (one extension), so any
  //    segment before the last that isn't an allowed type is suspicious.
  const hasSuspiciousIntermediate = intermediateSegments.some(
    (segment) => !allowedExtensions.includes(segment)
  );
  if (hasSuspiciousIntermediate) {
    return {
      valid: false,
      message: `Invalid file name. Files with multiple or disguised extensions are not allowed. Only ${allowedLabel} files are accepted.`,
    };
  }

  // 3. The real (final) extension must be in the allow-list. This is where a
  //    plain unsupported type (e.g. "image.svg") is rejected with a clear
  //    "extension not allowed" message.
  const realExtension = segments[segments.length - 1];
  if (!allowedExtensions.includes(realExtension)) {
    return {
      valid: false,
      message: `Invalid file extension. Only ${allowedLabel} files are allowed.`,
    };
  }

  // 4. The MIME type reported by the browser must match the real extension.
  const acceptedMimes = extensionMimeMap[realExtension] || [];
  if (file.type && !acceptedMimes.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      message: `Invalid file type. The file content does not match a valid ${allowedLabel} file.`,
    };
  }

  return { valid: true, message: "" };
};

/**
 * Validate an uploaded image file. Allows only real .png / .jpg / .jpeg files
 * and rejects deceptive/double-extension names.
 */
export const validateImageFile = (file: File): FileValidationResult => {
  return validateByExtensionMap(
    file,
    IMAGE_EXTENSION_MIME_MAP,
    "PNG, JPG and JPEG"
  );
};

/**
 * Generic validator for callers that need a custom extension -> MIME allow-list
 * (e.g. uploads that also permit video or pdf). `allowedLabel` is the text shown
 * in the error message, e.g. "PNG, JPG, MP4 and PDF".
 */
export const validateUploadedFile = (
  file: File,
  extensionMimeMap: Record<string, string[]>,
  allowedLabel: string
): FileValidationResult => {
  return validateByExtensionMap(file, extensionMimeMap, allowedLabel);
};

export default validateImageFile;
